import {
  Badge,
  ConfigProvider,
  Pagination,
  Segmented,
  Select,
  Skeleton,
  theme,
} from 'antd';
import dayjs from 'dayjs';
import React, { useState } from 'react';
import { useGetNotifications } from '../../../utils/api/notifications/useGetNotifications';
import { useMarkRead } from '../../../utils/api/notifications/useMarkRead';
import {
  getDateLabel,
  getNotificationIcon,
  hasNotificationError,
} from '../../../utils/const/notifications';
import { StyledPageFooter, StyledPageList, StyledPageRow } from './styled';

const SCOPE_OPTIONS = [
  { label: 'Globales', value: 'global' },
  { label: 'Personnelles', value: 'personal' },
];

const PAGE_SIZE_OPTIONS = [20, 50, 100].map((size) => ({
  value: size,
  label: `${size} / page`,
}));

const NotificationsPage = () => {
  const [scope, setScope] = useState('personal');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const {
    token: { colorPrimary, colorError, colorTextSecondary, colorTextTertiary },
  } = theme.useToken();

  const { notifications, count, isLoading } = useGetNotifications({
    scope,
    limit: pageSize,
    offset: (page - 1) * pageSize,
  });

  const { mutate: markRead } = useMarkRead();

  const handleScopeChange = (value) => {
    setScope(value);
    setPage(1);
  };

  const handlePageSizeChange = (value) => {
    setPageSize(value);
    setPage(1);
  };

  const items = notifications ?? [];

  const handleRowClick = (item) => {
    if (!item.read_at) {
      markRead({ id: item.id });
    }
  };

  const renderRow = (item, index) => {
    const Icon = getNotificationIcon(item.context);
    const isError = hasNotificationError(item.context);
    const dateLabel = getDateLabel(item.created_at);
    const previous = index > 0 ? items[index - 1] : null;
    const showDateLabel =
      !previous || getDateLabel(previous.created_at) !== dateLabel;

    return (
      <div key={item.id}>
        {showDateLabel && (
          <div
            style={{
              padding: '16px 12px 4px',
              fontSize: 12,
              color: colorTextTertiary,
            }}
          >
            {dateLabel}
          </div>
        )}
        <StyledPageRow onClick={() => handleRowClick(item)}>
          <Badge dot={!item.read_at}>
            <Icon
              style={{
                fontSize: 12,
                color: isError ? colorError : colorPrimary,
                background: '#fff',
                padding: '10px',
                borderRadius: '8px',
              }}
            />
          </Badge>
          <div
            style={{
              flex: '0 0 320px',
              fontWeight: item.read_at ? 'normal' : 'bold',
            }}
          >
            {item.title}
          </div>
          <div style={{ flex: 1, minWidth: 0, color: colorTextSecondary }}>
            {item.message}
          </div>
          <div
            style={{
              flex: '0 0 48px',
              textAlign: 'right',
              color: colorTextTertiary,
            }}
          >
            {dayjs(item.created_at).format('HH:mm')}
          </div>
        </StyledPageRow>
      </div>
    );
  };

  return (
    <>
      <ConfigProvider
        theme={{
          components: {
            Segmented: {
              itemSelectedBg: colorPrimary,
              itemSelectedColor: '#fff',
            },
          },
        }}
      >
        <Segmented
          size="large"
          options={SCOPE_OPTIONS}
          value={scope}
          onChange={handleScopeChange}
          style={{ marginBottom: 16 }}
        />
      </ConfigProvider>
      {isLoading ? (
        <Skeleton active />
      ) : (
        <StyledPageList>{items.map(renderRow)}</StyledPageList>
      )}
      <StyledPageFooter>
        <Select
          value={pageSize}
          onChange={handlePageSizeChange}
          options={PAGE_SIZE_OPTIONS}
        />
        <Pagination
          current={page}
          pageSize={pageSize}
          total={count ?? 0}
          onChange={setPage}
          showSizeChanger={false}
        />
      </StyledPageFooter>
    </>
  );
};

export default NotificationsPage;
